import { useTranslation } from 'react-i18next';
import { Flex, Heading, Text } from '@radix-ui/themes';
import { Callout, PillToggle } from '@/components/common';
import { WizardScreen } from '@/components/layout';
import { ContinueButton, OnboardingWizardHeader } from '@/components/onboarding';
import { V90_ETHNICITY_OPTIONS } from '@/constants';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { fieldLabelStyle } from './styles';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';
import { pillToggleDelta } from './utils';

const PREFER_NOT_TO_SAY = 'Prefer not to say';

const toggleEthnicity = (selected: string[], value: string): string[] => {
  if (value === PREFER_NOT_TO_SAY) {
    return selected.includes(PREFER_NOT_TO_SAY) ? [] : [PREFER_NOT_TO_SAY];
  }

  const withoutPrefer = selected.filter(item => item !== PREFER_NOT_TO_SAY);
  return withoutPrefer.includes(value)
    ? withoutPrefer.filter(item => item !== value)
    : [...withoutPrefer, value];
};

export function EthnicityStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation('onboarding');
  const selectedEthnicities = useOnboardingStore(state => state.selectedEthnicities);
  const setSelectedEthnicities = useOnboardingStore(state => state.setSelectedEthnicities);
  const saveAndNext = useSaveAndNext(flow);

  return (
    <WizardScreen header={<OnboardingWizardHeader flow={flow} onSkip={flow.skip} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('steps.ethnicity.title')}{' '}
          <Text size="1" color="gray" as="span">
            {t('steps.ethnicity.tag')}
          </Text>
        </Heading>
        <Callout>{t('steps.ethnicity.callout')}</Callout>
        <Flex direction="column" gap="1">
          <Text size="1" color="gray" style={fieldLabelStyle}>
            {t('steps.ethnicity.fieldLabel')}
          </Text>
          <PillToggle
            type="multiple"
            options={V90_ETHNICITY_OPTIONS}
            value={selectedEthnicities}
            onValueChange={next => {
              const clicked = pillToggleDelta(selectedEthnicities, next);
              if (clicked) setSelectedEthnicities(toggleEthnicity(selectedEthnicities, clicked));
            }}
          />
        </Flex>
        <ContinueButton onClick={saveAndNext}>{t('actions.continue')}</ContinueButton>
      </Flex>
    </WizardScreen>
  );
}
